/**
 * Lab → Referrals dashboard.
 *
 * Shows a lab everything about the referral program its patients use:
 *   - KPIs: total referrals, joined, completed, rewards paid
 *   - Table of individual referrals (who referred whom, status, reward)
 *   - Quick link to configure the program (Branding → Team & Programs)
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DashboardLayout } from "@/components/Layout";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Gift, Users, Trophy, IndianRupee, Loader2, Settings, Share2, Copy, Check, MessageCircle } from "lucide-react";
import { useAuth } from "@/lib/auth";

const STATUS_STYLE = {
  invited:   { label: "Invited",   cls: "bg-muted text-ink/70" },
  joined:    { label: "Joined",    cls: "bg-efgh-blue/10 text-efgh-blue" },
  completed: { label: "Completed", cls: "bg-efgh-green/10 text-efgh-green" },
};

export default function LabReferrals() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const [labSlug, setLabSlug] = useState("");
  useEffect(() => {
    Promise.all([
      api.get("/lab/referrals"),
      api.get("/lab/referral-program").catch(() => ({ data: {} })),
      user?.lab_id ? api.get(`/labs/${user.lab_id}`).catch(() => ({ data: {} })) : Promise.resolve({ data: {} }),
    ])
      .then(([r1, r2, r3]) => {
        setData(r1.data);
        setProgram(r2.data?.effective || r2.data || {});
        setLabSlug(r3.data?.slug || "");
      })
      .catch((e) => toast.error(apiError(e, "Could not load referrals")))
      .finally(() => setLoading(false));
  }, [user?.lab_id]);

  // Point patients at the lab's PUBLIC booking page (no login required) with a
  // ?ref query for attribution — new patients can book immediately without
  // being forced through login/registration first.
  const shareUrl = labSlug
    ? `${window.location.origin}/l/${labSlug}/book?ref=referral`
    : `${window.location.origin}/register`;
  const shareText = program?.description
    ? `${program.description} Sign up here → ${shareUrl}`
    : `Join us on EFGH — refer friends and earn rewards. Sign up here → ${shareUrl}`;

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 2000);
      toast.success("Referral link copied");
    });
  };
  const shareWhatsapp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, "_blank", "noopener");
  };

  if (loading) {
    return (
      <DashboardLayout role="lab_admin">
        <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-ink/40 mx-auto" /></div>
      </DashboardLayout>
    );
  }
  const stats = data?.stats || { total: 0, joined: 0, completed: 0, rewards_paid: 0 };
  const referrals = data?.referrals || [];

  return (
    <DashboardLayout role="lab_admin">
      <div className="max-w-6xl">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading font-bold text-3xl text-ink flex items-center gap-2">
              <Gift className="w-6 h-6 text-efgh-orange" /> Referrals
            </h1>
            <p className="text-ink/60 text-sm mt-1">Track who is referring new patients to your lab and how much reward you&apos;ve paid out.</p>
          </div>
          <Link to="/lab/settings?tab=programs">
            <Button variant="outline" data-testid="configure-referral"><Settings className="w-4 h-4 mr-2" /> Configure program</Button>
          </Link>
        </div>

        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="lab-referral-kpis">
          <Kpi icon={Users}   color="text-efgh-blue"   bg="bg-efgh-blue/10"   value={stats.total} label="Total referrals" />
          <Kpi icon={Trophy}  color="text-efgh-orange" bg="bg-efgh-orange/10" value={stats.joined} label="Referees joined" />
          <Kpi icon={Gift}    color="text-efgh-green"  bg="bg-efgh-green/10"  value={stats.completed} label="Completed" />
          <Kpi icon={IndianRupee} color="text-efgh-yellow" bg="bg-efgh-yellow/10" value={`₹${stats.rewards_paid || 0}`} label="Rewards paid" />
        </div>

        {/* Share the program */}
        <Card className="mt-6 p-6 rounded-2xl" data-testid="referral-share-card">
          <div className="flex items-center gap-2 mb-1">
            <Share2 className="w-5 h-5 text-efgh-orange" />
            <div className="font-heading font-semibold text-lg text-ink">Share your referral program</div>
          </div>
          <p className="text-sm text-ink/60">Send this link to your patients — they earn when a friend signs up and books a report at your lab.</p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[260px] flex items-center gap-2 rounded-xl border border-border bg-muted/30 px-3 py-2">
              <span className="text-xs text-ink/60 font-mono truncate flex-1" data-testid="referral-share-url">{shareUrl}</span>
              <button type="button" onClick={copyLink} data-testid="referral-copy" className="text-ink/60 hover:text-ink">
                {copied ? <Check className="w-4 h-4 text-success" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
            <Button data-testid="referral-whatsapp" onClick={shareWhatsapp} className="bg-[#25D366] hover:bg-[#20b957] text-white">
              <MessageCircle className="w-4 h-4 mr-2" /> Share on WhatsApp
            </Button>
          </div>
          {program?.enabled === false && (
            <div className="mt-3 p-3 rounded-lg bg-warning/10 text-warning text-xs">
              Your referral program is currently disabled — enable it in <Link to="/lab/settings?tab=programs" className="underline">Settings → Team & Programs</Link>.
            </div>
          )}
        </Card>

        <Card className="mt-6 p-0 rounded-2xl overflow-hidden">
          {referrals.length === 0 ? (
            <div className="p-10 text-center text-ink/50">
              <Gift className="w-10 h-10 mx-auto text-ink/20 mb-3" />
              <div className="font-heading font-semibold text-ink">No referrals yet</div>
              <div className="text-sm mt-1">Enable the referral program in <Link to="/lab/settings?tab=programs" className="underline text-efgh-orange">Settings → Team & Programs</Link> to start growing.</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="lab-referrals-table">
                <thead className="text-[10px] uppercase tracking-widest text-ink/50 font-heading bg-muted/40">
                  <tr>
                    <th className="text-left px-4 py-3">Referrer</th>
                    <th className="text-left px-4 py-3">Referred friend</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-right px-4 py-3">Reward paid</th>
                    <th className="text-left px-4 py-3">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {referrals.map((r) => {
                    const st = STATUS_STYLE[r.status] || STATUS_STYLE.invited;
                    return (
                      <tr key={r.id} className="border-t border-border/60 hover:bg-muted/30">
                        <td className="px-4 py-3">
                          <div className="font-semibold text-ink">{r.referrer?.name || "—"}</div>
                          <div className="text-[11px] text-ink/50">{r.referrer?.email}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-semibold text-ink">{r.referee?.name || "—"}</div>
                          <div className="text-[11px] text-ink/50">{r.referee?.email}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded font-semibold ${st.cls}`}>{st.label}</span>
                        </td>
                        <td className="px-4 py-3 text-right font-mono">{r.reward_credited ? `₹${r.reward_coins || 0}` : "—"}</td>
                        <td className="px-4 py-3 text-ink/60 text-xs">{(r.created_at || "").slice(0, 10)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </DashboardLayout>
  );
}

function Kpi({ icon: Icon, color, bg, value, label }) {
  return (
    <Card className="rounded-2xl p-5">
      <div className={`w-10 h-10 rounded-xl ${bg} ${color} grid place-items-center`}><Icon className="w-5 h-5" /></div>
      <div className="mt-3 font-heading font-bold text-3xl text-ink">{value}</div>
      <div className="text-xs uppercase tracking-wider text-ink/50 mt-1">{label}</div>
    </Card>
  );
}
